import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { Avatar, Button, ErrorBoundary, Icon, Modal, Sheet, type IconName } from "@astrum/ui";
import { api, SESSION_ENDED_EVENT } from "../lib/api.js";
import { reportError } from "../lib/error-report.js";
import { useAuth } from "../lib/auth.js";
import { brandInitials, useBranding } from "../lib/branding.js";
import { WorkspaceSwitcher } from "../components/WorkspaceSwitcher.js";
import { useWorkspace } from "../lib/workspace.js";
import { useCommandPalette } from "./CommandPalette.js";
import { NativeNotificationWarning } from "./NativeNotificationWarning.js";
import { DailyReportReminder } from "./DailyReportReminder.js";
import { DownloadApps } from "./DownloadApps.js";
import { Notifications } from "./Notifications.js";
import { PushPrompt } from "./PushPrompt.js";
import { FirstRunTour } from "../components/FirstRunTour.js";
import { ShortcutsSheet } from "./ShortcutsSheet.js";
import { ShortcutsContext } from "./shortcuts-context.js";
import { useReconnected, useRealtime, useRealtimeFrames } from "../lib/realtime-context.js";
import { useWhatsNewDot } from "../lib/whats-new.js";
import { useRail } from "./use-rail.js";
import { useUnreadChatTotal } from "./useUnreadChatTotal.js";
import { startNativePush } from "../lib/native-app.js";
import "./app-shell.css";

/**
 * Whether the viewer owes today's check-in report — drives a persistent dot on
 * the Daily report nav item and the Home banner, so the nudge is not only the
 * one transient 4:30pm notification a person can easily miss. Re-checked on
 * every route change (filing the report clears it without a refresh) and once
 * an hour so it flips on when the day rolls or the report window opens.
 */
function useOwesReport(): boolean {
  const { viewer } = useAuth();
  const location = useLocation();
  const [owes, setOwes] = useState(false);

  useEffect(() => {
    if (!viewer) return;
    let cancelled = false;
    const check = () =>
      api
        .get<{ owesReport: boolean }>("/me/checkin-status")
        .then((res) => {
          if (!cancelled) setOwes(res.owesReport);
        })
        .catch(() => undefined);
    check();
    const timer = setInterval(check, 60 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [viewer, location.pathname]);

  return owes;
}

/**
 * Tasks and Schedule badges: unread notifications grouped by what they point
 * at, keyed by `resourceType` ("task" or "event") — the same field the bell
 * panel already uses to decide where a click navigates.
 *
 * One shared fetch+subscribe rather than reading out of Notifications.tsx's
 * own state: that component owns audio, push, and desktop-notification side
 * effects tightly coupled to its internals, and duplicating just the count
 * here (mirroring useUnreadChatTotal's already-established pattern) is
 * simpler than threading a second consumer through it.
 */
function useUnreadByResourceType(): Record<string, number> {
  const { viewer } = useAuth();
  const location = useLocation();
  const [counts, setCounts] = useState<Record<string, number>>({});

  const load = useCallback(() => {
    if (!viewer) return;
    api
      .get<{ notifications: { resourceType: string | null }[] }>("/notifications?unreadOnly=true&limit=100")
      .then((res) => {
        const next: Record<string, number> = {};
        for (const n of res.notifications) {
          if (!n.resourceType) continue;
          next[n.resourceType] = (next[n.resourceType] ?? 0) + 1;
        }
        setCounts(next);
      })
      .catch(() => undefined);
  }, [viewer]);

  useEffect(() => load(), [load, location.pathname]);
  useReconnected(load);

  useRealtimeFrames((frame) => {
    /*
     * Something was read or removed, possibly in another session. Which
     * badges it belonged to is not in the frame, so this refetches the same
     * grouped counts navigation already loads rather than guessing at a
     * decrement — the badges drop without waiting for a route change.
     */
    if (frame.t === "notification_read") {
      load();
      return;
    }
    if (frame.t !== "notification" || !frame.notification.resourceType) return;
    const resourceType = frame.notification.resourceType;
    setCounts((prev) => ({ ...prev, [resourceType]: (prev[resourceType] ?? 0) + 1 }));
  });

  return counts;
}

/**
 * Feed badge: posts have no notification row at all (see WORKSPACE_TOPIC's
 * `post`/`post_comment` frames), so this counts live frames directly instead
 * of deriving from `/notifications`. Resets on arrival at `/feed` — there is
 * no server-side "last seen" for the feed to reload from, so unlike the other
 * badges this one only reflects activity since the tab was opened, not
 * across a reload.
 */
function useUnreadFeedTotal(): number {
  const { viewer } = useAuth();
  const location = useLocation();
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (location.pathname.startsWith("/feed")) setTotal(0);
  }, [location.pathname]);

  useRealtimeFrames((frame) => {
    if (location.pathname.startsWith("/feed")) return;
    if (frame.t === "post" && frame.post.authorId !== viewer?.id) {
      setTotal((prev) => prev + 1);
    } else if (frame.t === "post_comment" && frame.comment.authorId !== viewer?.id) {
      setTotal((prev) => prev + 1);
    }
  });

  return total;
}

/**
 * Whether the socket has been down long enough to say so.
 *
 * A blip — a reconnect that lands within a couple of seconds — passes
 * unannounced; flashing a banner for every one teaches people to ignore it.
 * Past that, a silently dead socket is worse than a visible one: without the
 * banner screens just quietly stop updating. Only the states the client is
 * trying to recover from count; `closed` (signed out) and `unauthenticated`
 * (handled by sending the person to sign in) do not.
 */
export const RECONNECTING_BANNER_DELAY_MS = 2_000;

function useShowReconnecting(status: string): boolean {
  const [show, setShow] = useState(false);
  const down = status === "connecting" || status === "reconnecting";
  // Track whether the socket has ever been open: the very first connect is a
  // page load, not a lost connection.
  const [everOpen, setEverOpen] = useState(status === "open");
  useEffect(() => {
    if (status === "open") setEverOpen(true);
  }, [status]);

  useEffect(() => {
    if (!down || !everOpen) {
      setShow(false);
      return;
    }
    const timer = setTimeout(() => setShow(true), RECONNECTING_BANNER_DELAY_MS);
    return () => clearTimeout(timer);
  }, [down, everOpen]);

  return show;
}

type NavItem = { to: string; icon: IconName; label: string };

const NAV: NavItem[] = [
  { to: "/home", icon: "home", label: "Home" },
  { to: "/chat", icon: "chat", label: "Chat" },
  { to: "/tasks", icon: "check", label: "Tasks" },
  { to: "/work", icon: "work", label: "Work" },
  { to: "/checklist", icon: "checklist", label: "Checklist" },
  { to: "/daily-report", icon: "clock", label: "Daily report" },
  { to: "/timesheet", icon: "clock", label: "Timesheet" },
  { to: "/insights", icon: "insights", label: "Insights" },
  { to: "/dashboards", icon: "insights", label: "Dashboards" },
  { to: "/goals", icon: "check", label: "Goals" },
  { to: "/forms", icon: "document", label: "Requests" },
  { to: "/schedule", icon: "calendar", label: "Schedule" },
  { to: "/people", icon: "people", label: "People" },
  { to: "/feed", icon: "pin", label: "Feed" },
  { to: "/files", icon: "document", label: "Files" },
];

/**
 * What the bottom tab bar shows directly on a phone.
 *
 * Four, not eight: a tab bar item needs roughly 70px to fit an icon and a
 * label without crowding, and a 375px phone has room for about five. The rest
 * — plus admin, plus signing out — live behind "More", the same way a phone's
 * own apps put overflow behind one more icon rather than shrinking every tab
 * until the labels touch.
 */
const MOBILE_PRIMARY_PATHS = new Set(["/home", "/chat", "/work", "/schedule"]);

/**
 * A distinct dark rail colour per office, so switching offices is obvious.
 *
 * Hand-picked solid dark shades rather than a hue rotated over the navy: a
 * tinted navy muddied the warm hues (China's brown read as dirty), so each
 * office instead gets its own self-contained colour chosen to look good as a
 * full rail. `bg` is the rail; `line` is a brighter top accent of the same
 * family. Assigned by the office's position in the viewer's list, so adjacent
 * offices never collide. The first entry (night, the V2 rail colour) matches the brand, so a
 * single-office viewer's rail is unchanged from before.
 */
const OFFICE_RAIL_COLORS: { bg: string; line: string }[] = [
  { bg: "#0a1a28", line: "#f47231" }, // brand night + orange (default office)
  { bg: "#12324a", line: "#38bdf8" }, // deep blue + sky
  { bg: "#0f3d34", line: "#2dd4bf" }, // deep teal + aqua
  { bg: "#3a1d47", line: "#c084fc" }, // deep plum + violet
  { bg: "#4a1d2e", line: "#fb7185" }, // deep wine + rose
  { bg: "#1c3a1f", line: "#4ade80" }, // deep forest + green
  { bg: "#3a2f12", line: "#fbbf24" }, // deep amber + gold
  { bg: "#2a2140", line: "#a5b4fc" }, // deep indigo + periwinkle
];

function officeRailColor(index: number): { bg: string; line: string } {
  const n = OFFICE_RAIL_COLORS.length;
  return OFFICE_RAIL_COLORS[((index % n) + n) % n]!;
}

/**
 * The persistent frame: a narrow icon rail on desktop, a bottom tab bar on
 * mobile. Deliberately the same component for both — the routes and state are
 * identical, only the chrome differs.
 */
export function AppShell() {
  const { viewer, signOut } = useAuth();
  const { workspaceName, workspaceLogo } = useBranding();
  // The active office, so its name can sit under the logo as a persistent tag.
  // Shown for everyone in an office — including single-office viewers, whom the
  // switcher above never renders for.
  const { active, workspaces } = useWorkspace();
  const { expanded, toggle } = useRail();
  const navigate = useNavigate();
  const palette = useCommandPalette();
  const [moreOpen, setMoreOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const shortcutsApi = useMemo(() => ({ show: () => setShortcutsOpen(true) }), []);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const { status: realtimeStatus } = useRealtime();
  const showReconnecting = useShowReconnecting(realtimeStatus);
  const unreadChatTotal = useUnreadChatTotal();
  const unreadByResourceType = useUnreadByResourceType();
  const unreadFeedTotal = useUnreadFeedTotal();
  const owesReport = useOwesReport();
  const { unseen: whatsNewDot } = useWhatsNewDot();
  const navBadges: Record<string, number> = {
    "/chat": unreadChatTotal,
    "/tasks": unreadByResourceType.task ?? 0,
    "/schedule": unreadByResourceType.event ?? 0,
    "/feed": unreadFeedTotal,
  };

  // The phone apps: register for push and open whatever notification was
  // tapped, including the one that launched the app. Here rather than in the
  // bell, which renders twice (rail and tab bar).
  useEffect(() => startNativePush(navigate), [navigate]);

  /*
   * A session that cannot be recovered ends at the sign-in screen.
   *
   * The realtime client reports `unauthenticated` when the server closes its
   * socket for want of a session — signed out elsewhere, revoked, or expired.
   * Retrying cannot fix that, so without this the app sat on "Reconnecting…"
   * indefinitely: still rendering a signed-in interface, silently receiving
   * nothing, and offering no way out. Handled here rather than per screen so
   * every route recovers the same way.
   */
  useEffect(() => {
    if (realtimeStatus !== "unauthenticated") return;
    // The same path as a refused request: sign-in says the session ended and
    // brings the person back to this page afterwards.
    window.dispatchEvent(new Event(SESSION_ENDED_EVENT));
  }, [realtimeStatus]);

  const primaryNav = NAV.filter((item) => MOBILE_PRIMARY_PATHS.has(item.to));
  const overflowNav = NAV.filter((item) => !MOBILE_PRIMARY_PATHS.has(item.to));

  // A distinct rail colour per office, so switching offices is unmistakable.
  // Assigned by the office's position in the viewer's list; needs nothing
  // stored. No office (still loading) leaves the tokens unset and the rail
  // renders exactly as before.
  const officeIndex = active ? workspaces.findIndex((w) => w.id === active.id) : -1;
  const railColor = officeIndex >= 0 ? officeRailColor(officeIndex) : null;
  const shellStyle = railColor
    ? ({ "--office-rail": railColor.bg, "--office-line": railColor.line } as CSSProperties)
    : undefined;

  return (
    <div className={`shell ${expanded ? "is-expanded" : ""}`} style={shellStyle}>
      {/* A real link would put a hash in the URL, which the router then has to
          undo; moving focus does the same job for keyboard users. */}
      <a
        className="skip-link"
        href="#main-content"
        onClick={(event) => {
          event.preventDefault();
          document.getElementById("main-content")?.focus();
        }}
      >
        Skip to main content
      </a>
      <nav className="shell-rail" aria-label="Sections">
        <div className="shell-rail-head">
          <NavLink to="/home" className="shell-logo" aria-label={`${workspaceName} home`} title={workspaceName}>
            {workspaceLogo ? (
              <img src={workspaceLogo} alt="" className="shell-logo-img" />
            ) : (
              brandInitials(workspaceName)
            )}
          </NavLink>
          {/* Only when expanded: the collapsed rail has no room for it, and the
              logo already carries the workspace's identity there. */}
          {expanded && <span className="shell-wordmark">{workspaceName}</span>}
        </div>

        {/* The active office, named under the logo — but only for someone in a
            single office, who gets no switcher. A multi-office viewer sees the
            office named by the switcher just below, so a tag here would repeat
            it three times over (logo wordmark, tag, switcher). Both paths still
            get the per-office hue on the rail. In the collapsed rail there is no
            room for the name, so it shows initials with the full name on hover. */}
        {active && workspaces.length <= 1 && (
          <div
            className="shell-office-tag"
            title={expanded ? undefined : active.name}
            aria-label={`Office: ${active.name}`}
          >
            {expanded ? active.name : brandInitials(active.name)}
          </div>
        )}

        {/* Sits between the identity and the sections: switching office is a
            change to which identity the whole rail is showing. Renders nothing
            for anyone who belongs to a single office. */}
        <div className="shell-rail-switcher">
          <WorkspaceSwitcher railExpanded={expanded} />
        </div>

        <div className="shell-nav">
          {NAV.map((item) => {
            const badge = navBadges[item.to] ?? 0;
            // A persistent dot when the viewer owes today's report — distinct
            // from the numeric unread badges, since it is a "you have something
            // to do", not a count.
            const owesDot = item.to === "/daily-report" && owesReport;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `shell-nav-item ${isActive ? "is-active" : ""}`}
                aria-label={
                  owesDot
                    ? `${item.label} — you haven't reported today`
                    : badge > 0
                      ? `${item.label} — ${badge} unread`
                      : item.label
                }
                title={expanded ? undefined : item.label}
              >
                <span className="shell-nav-icon">
                  <Icon name={item.icon} size={19} />
                  {badge > 0 && (
                    <span className="shell-nav-badge" aria-hidden="true">
                      {badge > 99 ? "99+" : badge}
                    </span>
                  )}
                  {owesDot && badge === 0 && <span className="shell-nav-dot" aria-hidden="true" />}
                </span>
                {/* Two ways of naming the same thing, never both at once: a
                    visible label when there is room, a hover tooltip when there
                    is not. Rendering both would read the label twice to a screen
                    reader. */}
                {expanded ? (
                  <span className="shell-nav-label">{item.label}</span>
                ) : (
                  <span className="shell-tip">{item.label}</span>
                )}
              </NavLink>
            );
          })}
        </div>

        <div className="shell-rail-foot">
          {/* Sits with the account controls rather than the content sections:
              administration is about the workspace, not a place to work. */}
          {viewer?.role === "admin" && (
            <NavLink
              to="/admin"
              className={({ isActive }) => `shell-nav-item ${isActive ? "is-active" : ""}`}
              aria-label="Administration"
              title={expanded ? undefined : "Administration"}
            >
              <Icon name="settings" size={18} />
              {expanded ? (
                <span className="shell-nav-label">Administration</span>
              ) : (
                <span className="shell-tip">Administration</span>
              )}
            </NavLink>
          )}
          {/* Below here the controls are actions rather than destinations, but
              when the rail is expanded they still need naming: an unlabelled
              icon sitting under a labelled row reads as an unfinished list. */}
          <Notifications railExpanded={expanded} />
          <button
            type="button"
            className="shell-rail-action"
            aria-label="Search — ⌘K"
            title={expanded ? undefined : "Search — ⌘K"}
            onClick={palette.open}
          >
            <Icon name="search" size={18} />
            {expanded && <span className="shell-nav-label">Search</span>}
          </button>
          <DownloadApps expanded={expanded} />
          <NavLink
            to="/help"
            className={({ isActive }) => `shell-rail-action ${isActive ? "is-active" : ""}`}
            aria-label="Help"
            title={expanded ? undefined : "Help"}
          >
            <Icon name="info" size={18} />
            {expanded && <span className="shell-nav-label">Help</span>}
          </NavLink>
          <button
            className="shell-me shell-rail-action"
            onClick={() => navigate("/profile")}
            aria-label={`${viewer?.displayName ?? "Profile"} — open profile`}
            title={expanded ? undefined : viewer?.displayName}
          >
            <Avatar name={viewer?.displayName ?? "?"} size={29} />
            {whatsNewDot && <span className="shell-me-dot" aria-hidden="true" />}
            {expanded && (
              <span className="shell-nav-label">{viewer?.displayName ?? "Profile"}</span>
            )}
          </button>
          <button
            type="button"
            className="shell-rail-action shell-rail-toggle"
            aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
            title={expanded ? undefined : "Expand sidebar"}
            onClick={toggle}
            aria-expanded={expanded}
          >
            <Icon name={expanded ? "chevron-left" : "chevron-right"} size={18} />
            {expanded && <span className="shell-nav-label">Collapse</span>}
          </button>
        </div>
      </nav>

      <main className="shell-main" id="main-content" tabIndex={-1}>
        {showReconnecting && (
          <div className="shell-reconnecting" role="status">
            Reconnecting…
          </div>
        )}
        {/* Per-screen boundary: a render error in one route shows a recoverable
            fallback while the nav rail keeps working, and navigating elsewhere
            (a new pathname) resets it automatically. */}
        <ErrorBoundary resetKeys={[location.pathname]} onError={(error) => reportError(error)}>
          <ShortcutsContext.Provider value={shortcutsApi}>
            <Outlet />
          </ShortcutsContext.Provider>
        </ErrorBoundary>
      </main>

      {/* Renders nothing unless push is configured, supported, and off — see
          PushPrompt for why it is asked here rather than only in Profile. */}
      <PushPrompt />

      {/* First sign-in only (server-side flag); also restartable from Help. */}
      <FirstRunTour />
      <ShortcutsSheet open={shortcutsOpen} onOpen={() => setShortcutsOpen(true)} onClose={() => setShortcutsOpen(false)} />

      {/* Renders nothing until a real streak of failed native popups this
          session — see NativeNotificationWarning and
          native-notification-health.ts. */}
      <NativeNotificationWarning />

      {/* Renders nothing unless the viewer owes today's daily report and has
          not snoozed it — a harder-to-ignore prompt than the rail's red dot. */}
      <DailyReportReminder />

      {/* Mobile: the rail is hidden and this takes over. Only four
          destinations directly — a tab needs room for an icon and a label,
          and a 375px phone does not have room for the whole rail. Everything
          else lives behind "More". */}
      <nav className="shell-tabs" aria-label="Sections">
        {primaryNav.map((item) => {
          const badge = navBadges[item.to] ?? 0;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `shell-tab ${isActive ? "is-active" : ""}`}
            >
              <span className="shell-nav-icon">
                <Icon name={item.icon} size={21} />
                {badge > 0 && (
                  <span className="shell-nav-badge" aria-hidden="true">
                    {badge > 99 ? "99+" : badge}
                  </span>
                )}
              </span>
              <span>{item.label}</span>
            </NavLink>
          );
        })}
        {/* A fifth tab rather than a More-sheet entry: the bell needs its own
            popover (the same one the desktop rail uses), and burying it a
            level deeper behind "More" is exactly the unreachable state this
            was added to fix. */}
        <div className="shell-tab shell-tab--notifications">
          <Notifications railExpanded={false} />
          <span aria-hidden="true">Alerts</span>
        </div>
        <button type="button" className="shell-tab" onClick={() => setMoreOpen(true)} aria-label="More">
          <Icon name="more" size={21} />
          <span>More</span>
        </button>
      </nav>

      <Sheet open={moreOpen} onClose={() => setMoreOpen(false)} title="More">
        <div className="shell-more">
          {overflowNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className="shell-more-item"
              onClick={() => setMoreOpen(false)}
            >
              <Icon name={item.icon} size={19} />
              <span>{item.label}</span>
            </NavLink>
          ))}
          {viewer?.role === "admin" && (
            <NavLink to="/admin" className="shell-more-item" onClick={() => setMoreOpen(false)}>
              <Icon name="settings" size={19} />
              <span>Administration</span>
            </NavLink>
          )}
          <NavLink to="/help" className="shell-more-item" onClick={() => setMoreOpen(false)}>
            <Icon name="info" size={19} />
            <span>Help</span>
          </NavLink>
          <button
            type="button"
            className="shell-more-item"
            onClick={() => {
              setMoreOpen(false);
              navigate("/profile");
            }}
          >
            <Avatar name={viewer?.displayName ?? "?"} size={19} />
            <span>{viewer?.displayName ?? "Profile"}</span>
            {whatsNewDot && <span className="shell-me-dot shell-me-dot--inline" aria-label="New since you last looked" />}
          </button>
          <button
            type="button"
            className="shell-more-item is-destructive"
            onClick={() => {
              setMoreOpen(false);
              setConfirmSignOut(true);
            }}
          >
            <span className="shell-more-item__icon-spacer" aria-hidden="true" />
            <span>Sign out</span>
          </button>
        </div>
      </Sheet>

      <Modal open={confirmSignOut} onClose={() => setConfirmSignOut(false)} title="Sign out?">
        <div className="shell-signout-confirm">
          <p>You will need to sign in again to get back in.</p>
          <div className="shell-signout-confirm-actions">
            <Button variant="ghost" onClick={() => setConfirmSignOut(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => void signOut()}>
              Sign out
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
